import {
  isDevMode,
  LOCALE_ID,
  ModuleWithProviders,
  NgModule,
  Optional,
  SkipSelf,
  inject,
  provideAppInitializer,
} from "@angular/core";
import { ServiceWorkerModule } from "@angular/service-worker";
import {
  provideTranslateService,
  TranslatePipe,
  TranslateDirective,
  TranslateService,
} from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from "@angular/common/http";
import { OAuthModule } from "angular-oauth2-oidc";
import { environment } from "../../environments/environment";
import {
  AuthService,
  PluginInitializerService,
  AuthInterceptor,
  createPluginInitializer,
  resolveActiveLocale,
} from "core-client/api";
import { CorePluginService } from "core-plugin";
import {
  FullscreenOverlayContainer,
  OverlayContainer,
} from "@angular/cdk/overlay";
import localeDe from "@angular/common/locales/de";
import localeEn from "@angular/common/locales/en";
import { registerLocaleData } from "@angular/common";
import { BrowserModule } from "@angular/platform-browser";
import {
  OPTIONAL_PLUGIN_PROVIDERS,
  OPTIONAL_TOKEN_PROVIDERS,
} from "./active-plugins";

function authAppInitializerFactory(
  authService: AuthService,
  configurationService: PluginInitializerService,
): () => Promise<void> {
  return () =>
    configurationService.queueRegister("auth", async () => {
      await authService.initialize();
    });
}

// Import german as language for the angular templates
registerLocaleData(localeDe, "de");
registerLocaleData(localeEn, "en");

@NgModule({
  declarations: [],
  exports: [TranslatePipe, TranslateDirective],
  imports: [
    BrowserModule,
    TranslatePipe,
    TranslateDirective,
    OAuthModule.forRoot(),
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: "registerWhenStable:30000",
    }),
  ],
  providers: [
    AuthService,
    provideTranslateService({
      fallbackLang: "de",
      loader: provideTranslateHttpLoader({
        prefix: "./assets/i18n/",
        suffix: ".json",
      }),
    }),
    // Bind Angular's LOCALE_ID to the active translate language so every
    // date/number pipe formats in the selected language (falls back to the
    // configured fallback language, i.e. German). Resolved once at startup.
    {
      provide: LOCALE_ID,
      useFactory: (translate: TranslateService) => resolveActiveLocale(translate),
      deps: [TranslateService],
    },
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
  ],
})
export class CoreModule {
  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        {
          provide: "ENVIRONMENT",
          useValue: environment,
          multi: true,
        },
        createPluginInitializer("corePlugin", CorePluginService, {
          mandatory: true,
        }),
        ...OPTIONAL_TOKEN_PROVIDERS,
        ...OPTIONAL_PLUGIN_PROVIDERS,
        provideAppInitializer(() => {
          const initializerFn = authAppInitializerFactory(
            inject(AuthService),
            inject(PluginInitializerService),
          );
          return initializerFn();
        }),
      ],
    };
  }

  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error(
        "CoreModule is already loaded. Import it in the AppModule only",
      );
    }
  }
}
